/**
 * Deduplicate Lines Tool
 *
 * Removes duplicate lines with options for case sensitivity and order preservation.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const DeduplicateLinesTool: ITransformerTool = {
  id: 'deduplicate-lines',
  name: 'Deduplicate Lines',
  description: 'Remove duplicate lines from text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['deduplicate', 'duplicate', 'unique', 'lines', 'remove', 'text'],

  seo: {
    slug: 'deduplicate-lines',
    pageTitle: 'Deduplicate Lines - Free Online Tool | SWE DevTools',
    metaDescription: 'Remove duplicate lines from text instantly. Supports case-sensitive comparison and order preservation. Fast, private, runs entirely in your browser.',
    keywords: ['deduplicate lines', 'remove duplicate lines', 'unique lines online', 'line deduplication tool'],
    relatedTools: ['sort-lines', 'remove-empty-lines', 'word-count'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'caseSensitive',
      type: 'boolean',
      label: 'Case Sensitive',
      defaultValue: true,
      description: 'Treat lines with different casing as distinct',
    },
    {
      name: 'trimWhitespace',
      type: 'boolean',
      label: 'Trim Whitespace',
      defaultValue: false,
      description: 'Trim leading/trailing whitespace before comparing',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const caseSensitive = config.caseSensitive !== false;
    const trimWhitespace = Boolean(config.trimWhitespace);

    const lines = input.split('\n');
    const seen = new Set<string>();
    const result: string[] = [];

    for (const line of lines) {
      let key = trimWhitespace ? line.trim() : line;
      if (!caseSensitive) {
        key = key.toLowerCase();
      }
      if (!seen.has(key)) {
        seen.add(key);
        result.push(line);
      }
    }

    return result.join('\n');
  },
};
